import { useEffect, useRef } from 'react';

import { css } from '@compiled/react';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { token } from '@atlaskit/tokens';

import type { Recorder } from '../utilities/types';

const popupWrapperCss = css({
	position: 'absolute',
	insetBlockStart: '100%',
	insetInlineStart: 0,
	insetInlineEnd: 0,
	marginBlockStart: token('space.100'),
	zIndex: 1100,
});

const popupCss = css({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: token('elevation.surface.overlay'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: '8px',
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
});

const listCss = css({
	listStyle: 'none',
	margin: 0,
	padding: 0,
	display: 'flex',
	flexDirection: 'column',
});

const dividerCss = css({
	height: '1px',
	backgroundColor: token('color.border'),
	margin: 0,
	border: 'none',
});

const optionCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
	paddingBlock: token('space.200'),
	paddingInline: token('space.250'),
	cursor: 'pointer',
	backgroundColor: 'transparent',
	border: 'none',
	textAlign: 'left',
	width: '100%',
	'&:hover, &:focus-visible': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		outline: 'none',
	},
});

const appleIconCss = css({
	flexShrink: 0,
	color: token('color.text'),
});

const optionTextWrapperCss = css({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	minWidth: 0,
});

// "Need to download an older version?" text: black (default text color, not
// `color.link`) and forced onto a single line so the chevron sits inline
// with the full label.
const olderVersionTextCss = css({
	font: token('font.body'),
	color: token('color.text'),
	whiteSpace: 'nowrap',
});

const chevronWrapperCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	color: token('color.icon.subtle'),
	flexShrink: 0,
});

const AppleLogoIcon = () => (
	<svg
		css={appleIconCss}
		width="20"
		height="24"
		viewBox="0 0 20 24"
		fill="currentColor"
		aria-hidden="true"
	>
		<path d="M16.365 12.735c-.03-3.015 2.46-4.46 2.57-4.53-1.4-2.05-3.58-2.33-4.36-2.36-1.86-.19-3.63 1.1-4.58 1.1-.95 0-2.4-1.07-3.95-1.04-2.03.03-3.9 1.18-4.95 3-2.11 3.66-.54 9.08 1.52 12.05 1.01 1.46 2.21 3.09 3.78 3.03 1.52-.06 2.09-.98 3.93-.98s2.36.98 3.96.95c1.64-.03 2.68-1.48 3.68-2.95 1.16-1.69 1.64-3.33 1.66-3.42-.04-.02-3.18-1.22-3.21-4.85zM13.44 3.9c.84-1.02 1.41-2.43 1.25-3.84-1.21.05-2.67.81-3.54 1.82-.78.9-1.46 2.34-1.28 3.72 1.35.1 2.73-.69 3.57-1.7z" />
	</svg>
);

const titleCss = css({
	font: token('font.body'),
	fontWeight: token('font.weight.semibold'),
	color: token('color.text'),
});

const subtitleCss = css({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	marginBlockStart: token('space.025'),
});

export const DownloadMacOptionsDropdown = ({
	handleClick,
	onNeedHelp,
	onClose,
}: {
	handleClick: (recorder: Recorder) => void;
	onNeedHelp: () => void;
	onClose: () => void;
}) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;
			if (wrapperRef.current && target && !wrapperRef.current.contains(target)) {
				onClose();
			}
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('touchstart', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('touchstart', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onClose]);

	return (
		<div ref={wrapperRef} css={popupWrapperCss} data-test-id="download-mac-options-dropdown">
			<div css={popupCss}>
				<ul css={listCss} aria-label="Download the Loom desktop app">
					<li>
						<button
							type="button"
							css={optionCss}
							onClick={() => handleClick('mac')}
							aria-label="Download for Mac, macOS 15 or later required"
						>
							<AppleLogoIcon />
							<span css={optionTextWrapperCss}>
								<span css={titleCss}>Download for Mac</span>
								<span css={subtitleCss}>macOS 15 + required</span>
							</span>
						</button>
					</li>
					<li aria-hidden="true">
						<hr role="presentation" css={dividerCss} />
					</li>
					<li>
						<button type="button" css={optionCss} onClick={onNeedHelp}>
							<span css={olderVersionTextCss}>Need to download an older version?</span>
							<span css={chevronWrapperCss}>
								<ChevronRightIcon label="" color="currentColor" />
							</span>
						</button>
					</li>
				</ul>
			</div>
		</div>
	);
};
